import {
	ClientError,
	NotificationStyle,
	NotificationType,
	ProgressIcon,
} from '~/shared';

import { IObservable, Subject } from '../../utils';
import {
	INotificationState,
	INotificationsService,
	IShowNotificationOptions,
} from './notificationsServiceTypes';

export abstract class AbstractNotificationsService
	implements INotificationsService
{
	private readonly msPerTransition = 200;
	private readonly minDuration = 1500;
	private readonly maxDuration = 10000;
	private readonly maxVisibleNotifications = 3;

	private progressTimeout: ReturnType<typeof setTimeout> | null = null;
	private currentNotification = new Subject<INotificationState | null>(null);
	private displayedNotifications = new Subject<INotificationState[]>([]);
	private notificationsStack: {
		id: number;
		notification: INotificationState;
	}[] = [];
	private hideTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
	private notificationId = 0;
	private progressActive: boolean = false;

	public getDisplayedNotification(): IObservable<INotificationState | null> {
		return this.currentNotification;
	}

	public getDisplayedNotifications(): IObservable<INotificationState[]> {
		return this.displayedNotifications;
	}

	public showException(error: unknown): void {
		let text = 'Внутренняя ошибка';

		if (error instanceof ClientError) {
			const params = error.getParams();

			if (params?.code === 'ERR_BAD_REQUEST' && params?.method) {
				console.error(
					`API request error. Does endpoint "${params?.method}" exist?`,
				);
			}

			text = error.message ?? text;
		}

		console.error((error as any).message);

		this.showNotification({
			style: NotificationStyle.Error,
			type: NotificationType.Default,
			text,
		});
	}

	public showError(text: string): void {
		this.showNotification({
			style: NotificationStyle.Error,
			type: NotificationType.Default,
			text,
		});
	}

	public showSuccess(text: string): void {
		this.showNotification({
			style: NotificationStyle.Success,
			type: NotificationType.Default,
			text,
		});
	}

	public showInfo(text: string): void {
		this.showNotification({
			style: NotificationStyle.Info,
			type: NotificationType.Default,
			text,
		});
	}

	public showCounter(text: string, count: number, image: string) {
		this.showNotification({
			style: NotificationStyle.Counter,
			type: NotificationType.Counter,
			text,
			count,
			image,
		});
	}

	protected showNotification(notification: IShowNotificationOptions): void {
		// Не показываем уведомления, если отображается уведомление с прогрессом
		if (this.progressActive) {
			return;
		}

		if (
			this.notificationsStack.some((item) =>
				this.isDuplicateNotification(item.notification, notification),
			)
		) {
			return;
		}

		const nextEntry = {
			id: ++this.notificationId,
			notification,
		};
		this.notificationsStack = [nextEntry, ...this.notificationsStack];

		if (this.notificationsStack.length > this.maxVisibleNotifications) {
			const dropped = this.notificationsStack.splice(
				this.maxVisibleNotifications,
			);
			for (const item of dropped) {
				this.clearHideTimeout(item.id);
			}
		}

		this.emitVisibleNotifications();
		this.scheduleHide(nextEntry.id, notification);
	}

	public showProgress(
		text: string,
		duration: number = 3000,
		icon?: ProgressIcon,
	): void {
		this.currentNotification.next(null);
		this.displayedNotifications.next([]);

		this.clearProgressTimeout();

		this.progressTimeout = setTimeout(() => {
			this.currentNotification.next({
				text,
				duration: duration - 450,
				icon,
			});

			this.progressTimeout = setTimeout(
				() => this.hideProgress(),
				duration - 450,
			);
		}, 450);

		this.progressActive = true;
	}

	public hideProgress(): void {
		if (!this.progressActive) {
			return;
		}

		this.clearProgressTimeout();

		this.progressActive = false;
		this.currentNotification.next(null);

		this.progressTimeout = setTimeout(() => {
			this.emitVisibleNotifications();
		}, 450);
	}

	private emitVisibleNotifications(): void {
		const notifications = this.notificationsStack.map((item) => ({
			...item.notification,
			id: item.id,
		}));
		if (!this.progressActive) {
			this.displayedNotifications.next(notifications);
			this.currentNotification.next(notifications[0] ?? null);
		}
	}

	private scheduleHide(id: number, notification: INotificationState): void {
		const duration = this.getNotificationDuration(notification);
		const timeout = setTimeout(() => {
			this.hideTimeouts.delete(id);
			this.notificationsStack = this.notificationsStack.filter(
				(item) => item.id !== id,
			);
			this.emitVisibleNotifications();
		}, duration + this.msPerTransition);
		this.hideTimeouts.set(id, timeout);
	}

	private clearHideTimeout(id: number): void {
		const timeout = this.hideTimeouts.get(id);
		if (!timeout) {
			return;
		}

		clearTimeout(timeout);
		this.hideTimeouts.delete(id);
	}

	private clearProgressTimeout(): void {
		if (this.progressTimeout === null) {
			return;
		}

		clearTimeout(this.progressTimeout);
		this.progressTimeout = null;
	}

	private getNotificationDuration(notification: INotificationState): number {
		if (notification.duration !== undefined) {
			return notification.duration;
		}

		const reactionTime = 210;
		const avgWordLength = 5.28;
		const avgWordPerMinute = 180;
		const avgWords = notification.text.length / avgWordLength;
		const complexityFactor =
			notification.text.includes('.') || notification.text.includes(',')
				? 1.2
				: 1;
		const duration =
			((avgWords / avgWordPerMinute) * 60 * 1000 + reactionTime) *
			complexityFactor;

		return Math.round(
			Math.min(Math.max(duration, this.minDuration), this.maxDuration),
		);
	}

	private isDuplicateNotification(
		queued: INotificationState,
		incoming: INotificationState,
	): boolean {
		if (
			queued.type === NotificationType.Counter &&
			incoming.type === NotificationType.Counter
		) {
			return (
				queued.text === incoming.text &&
				queued.count === incoming.count &&
				queued.image === incoming.image
			);
		}

		return queued.text === incoming.text;
	}
}
